import React, { useEffect, useState } from 'react'
import { TemplateFormContainer } from './style'
import { TaskElementProps } from './Actions'
import { CampaignTaskElements } from 'services/api/campaignTemplates'
import TemplateTask from './TemplateTask'
import { useFormikContext } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { TemplateDetailSchema } from '../TemplateDetail'
import OnFormikValueChange from '../OnFormikValueChange'
import { updateCampaignTaskElements, CampaignTemplateStep } from 'state/campaignTemplates'
import { useNewTranslation } from 'hooks'
import { fieldKeyByTasks } from '../TemplateDetail/options'
import { updateFollowStep1Data, updateFollowInterpolationListData } from 'state/appPreview/action/follow'
import { selectTemplateFirstStepDataByTaskIndex } from 'state/appPreview/selector'
import PlaceholderComponent from 'components/AppPreview/common/InterpolationList'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { selectCampaignByTemplateData } from 'state'

const FollowTask = ({
    showError: baseShowError,
    taskOrder,
    isReadOnly,
    campaignTemplateStepKey,
    ...props
}: TaskElementProps) => {
    const fieldNames = fieldKeyByTasks[campaignTemplateStepKey]

    const [t] = useNewTranslation()
    const { values } = useFormikContext<TemplateDetailSchema>()
    const dispatch = useDispatch()
    const selectedTemplateFirstStepData = useSelector(selectTemplateFirstStepDataByTaskIndex(taskOrder))
    const campaign = useSelector(selectCampaignByTemplateData)
    const namePrefix = `task_elements.${taskOrder}.`
    const validateFieldsKey = fieldNames.map(field => namePrefix + field) as (keyof TemplateDetailSchema)[]
    const wrongFormatCopy = t('campaign.template.downloadAppFrom.errorMessage.wrongFormate')
    const isCampaignLive = campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE
    const [showError, setShowError] = useState(baseShowError)

    useEffect(() => {
        handleDebouncedChange(values)
    }, [])

    useEffect(() => {
        setShowError(baseShowError)
    }, [baseShowError])

    const disableAutoSave = () => {
        // Enable data capture and preview update if campaign is live
        if (isCampaignLive) {
            return false
        }

        return isReadOnly
    }

    const onSaveData = (fieldsValues: Partial<TemplateDetailSchema>, showSuccessToast: boolean) => {
        const validTemplateDataUuid = typeof props.templateUuid === 'string' ? props.templateUuid : ''
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        dispatch(
            updateCampaignTaskElements({
                templateUuid: validTemplateDataUuid,
                ...taskValue,
                setNavigationDisable: props.setNavigationDisable,
                showSuccessToast,
            }),
        )
    }

    const getStepInformation = (): {
        requirements?: string
        photoSubmissonTitle?: string
        maxPhotoSubmission?: number
        requirementTitle?: string
    } => {
        return {
            requirements: selectedTemplateFirstStepData.step?.blocks?.[0].description ?? '',
            requirementTitle: selectedTemplateFirstStepData.step?.blocks?.[0].title ?? '',
            photoSubmissonTitle: selectedTemplateFirstStepData.step?.blocks?.[1]?.title ?? '',
            maxPhotoSubmission: selectedTemplateFirstStepData.step?.blocks?.[1]?.max_photos,
        }
    }

    const handleDebouncedChange = (fieldsValues: Partial<TemplateDetailSchema>) => {
        const taskValue = (fieldsValues?.task_elements?.[taskOrder] ?? {}) as CampaignTaskElements
        const brandName = values?.task_elements?.[taskOrder].brand_name ?? ''
        if (taskValue.element) {
            const taskTitle = selectedTemplateFirstStepData?.taskTitle ?? ''
            const { requirements, photoSubmissonTitle, maxPhotoSubmission, requirementTitle } = getStepInformation()

            const mediaBlockTitle = photoSubmissonTitle ? (
                <PlaceholderComponent
                    template={photoSubmissonTitle ?? ''}
                    values={[
                        {
                            brand_name: brandName,
                            emptyValue: t('manageTeams.teamPage.brandNameTableLabel'),
                            isInline: true,
                        },
                        {
                            emptyValue: t('campaignCategory.socialMediaPlatformTitle'),
                            follow_platform: taskValue.follow_platform,
                            isInline: true,
                        },
                    ]}
                />
            ) : undefined

            const payload = {
                stepTitle: selectedTemplateFirstStepData.step?.title,
                mediaBlockTitle,
                title: requirementTitle,
                mediaBlockMaxPhotos: maxPhotoSubmission,
                follow_platform: taskValue.follow_platform,
                follow_platform_link: taskValue.follow_platform_link,
                brand_name: brandName,
            }

            const interpolationListPayload = {
                description: {
                    template: requirements ?? '',
                    values: [
                        {
                            brand_name: brandName,
                            emptyValue: t('manageTeams.teamPage.brandNameTableLabel'),
                            isInline: true,
                        },
                        {
                            follow_platform: taskValue?.follow_platform,
                            emptyValue: t('campaign.template.platform.appPreview.placeholder'),
                            isInline: true,
                        },
                        {
                            follow_platform_link: taskValue?.follow_platform_link,
                            emptyValue: t('campaign.template.link.appPreview.placeholder'),
                            isInline: true,
                            isUrl: true,
                        },
                    ],
                },
                taskTitle: {
                    template: taskTitle ?? '',
                    values: [
                        {
                            brand_name: brandName,
                            emptyValue: t('manageTeams.teamPage.brandNameTableLabel'),
                            isInline: true,
                        },
                    ],
                },
            }

            dispatch(updateFollowStep1Data(payload))
            dispatch(updateFollowInterpolationListData(interpolationListPayload))

            if (!isCampaignLive && !isReadOnly) {
                onSaveData(fieldsValues, false)
            }
        }
    }

    const onShowError = () => {
        setShowError(true)
    }

    return (
        <TemplateFormContainer data-testid="test-follow-task-page">
            <OnFormikValueChange
                callback={handleDebouncedChange}
                fields={validateFieldsKey}
                skipCallbackError={{
                    [namePrefix + 'follow_platform_link']: [wrongFormatCopy],
                }}
                validateSpecificField={props.validateSpecificField}
                showError={showError}
                updateCurrentStepStatus={props.updateCurrentStepStatus}
                setDisableNavigation={props.setNavigationDisable}
                disableAutoSave={disableAutoSave()}
            />

            <TemplateTask
                elementKey={campaignTemplateStepKey}
                field_keys={fieldNames}
                taskOrder={taskOrder}
                showError={showError}
                identifierPrefix={CampaignTemplateStep.FOLLOW}
                campaignTemplateStepKey={campaignTemplateStepKey}
                onShowError={onShowError}
                onSaveData={onSaveData}
                fields={validateFieldsKey}
                {...props}
            />
        </TemplateFormContainer>
    )
}

export default FollowTask
